import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Home, BedDouble, Car, Waves, Zap, Wind, ThermometerSun, DoorClosed } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../context/LanguageContext';
const heroImages = ['https://cdn.magicpatterns.com/uploads/7u4YYy5PgooNiUw5cpsCMF/ChatGPT_Image_8_jun_2026,_16_20_20.png', 'https://cdn.magicpatterns.com/uploads/6zKXuFxw9GCeixwwJVePkB/ChatGPT_Image_8_jun_2026,_16_22_07.png', 'https://cdn.magicpatterns.com/uploads/sEaLj2yBJeYZphciqLxHfC/ChatGPT_Image_8_jun_2026,_16_23_53.png', 'https://cdn.magicpatterns.com/uploads/s1J6oxkt7uC9RBM1TrMUw2/ChatGPT_Image_8_jun_2026,_16_25_19.png', 'https://cdn.magicpatterns.com/uploads/kZUtDdMVZiGmtggygT7C3X/ChatGPT_Image_8_jun_2026,_16_27_11.png'];
export function Hero() {
  const {
    t
  } = useLanguage();
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, 400]);
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  const scrollTo = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const indicatorIcons = [Home, BedDouble, Car, Waves, Wind, ThermometerSun, DoorClosed, Zap];
  const indicators = indicatorIcons.map((icon, i) => ({
    icon,
    text: t.hero.indicators[i]
  }));
  return <section className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden" data-id="element-216">
      {/* Background with Parallax */}
      <motion.div style={{
      y
    }} className="absolute inset-0 z-0" data-id="element-217">
        <div className="absolute inset-0 bg-navy/40 z-10" data-id="element-218" />{' '}
        {/* Dark overlay for text readability */}
        <AnimatePresence mode="sync" data-id="element-219">
          <motion.img key={current} src={heroImages[current]} alt="Residencial Aneto" initial={{
          opacity: 0,
          scale: 1.08
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 1.4
          },
          scale: {
            duration: 6,
            ease: 'linear'
          }
        }} className="absolute inset-0 w-full h-[120%] object-cover object-center -mt-[10%]" data-id="element-220" />
        </AnimatePresence>
      </motion.div>

      {/* Content */}
      <div className="container relative z-20 mx-auto px-6 md:px-12 text-center mt-20" data-id="element-221">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-222">
          <h1 className="text-5xl md:text-7xl lg:text-8xl text-white font-serif mb-4 drop-shadow-lg tracking-tight" data-id="element-223">
            {t.hero.title}
          </h1>
          <h2 className="text-2xl md:text-3xl lg:text-4xl text-white font-light mb-6 drop-shadow-md" data-id="element-224">
            {t.hero.subtitle}
          </h2>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-10 font-light leading-relaxed" data-id="element-225">
            {t.hero.desc}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-226">
            <Button size="lg" variant="secondary" onClick={() => scrollTo('#disponibilidad')} data-id="element-227">
              {t.hero.ctaPrimary}
            </Button>
            <Button size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-navy" onClick={() => scrollTo('#contacto')} data-id="element-228">
              {t.hero.ctaSecondary}
            </Button>
          </div>
        </motion.div>

        {/* Indicators */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="hidden md:flex flex-wrap justify-center gap-x-8 gap-y-4 lg:gap-x-12 bg-navy/80 backdrop-blur-sm py-8 px-10 rounded-2xl border border-white/10 max-w-6xl mx-auto" data-id="element-229">
          {indicators.map((item, index) => <div key={index} className="flex items-center gap-3 text-white" data-id="element-230">
              <item.icon className="w-5 h-5 text-sand flex-shrink-0" data-id="element-231" />
              <span className="text-sm font-medium tracking-wide" data-id="element-232">
                {item.text}
              </span>
            </div>)}
        </motion.div>
      </div>
    </section>;
}